import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useSelectUserContext, useIsFeatureEnabled } from '@innovyze/stylovyze';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ChatMessage } from '@/components/chat/';
import { FileUploadItem } from './FileUploadItem';
import { sendPrompt } from '@/services/chatApi';
import {
  uploadAttachment,
  isVideoFile,
  isImportFile,
} from '@/services/attachmentsApi';
import type {
  ChatMessage as ChatMessageType,
  FileUploadState,
  PromptAttachment,
} from '@/types/chat';
import { ScrollArea } from '../ui/scroll-area';
import { Sparkles, Paperclip, X, ArrowDown, BookOpen } from 'lucide-react';
// Deep imports, not the barrel: webpack.lib.js emits CommonJS, and the
// package's CJS barrel re-exports all ~320 icons opaquely to the tree shaker,
// which would ship every glyph in the published dist/Pages.js.
import Send from '@weave-brand/icon/icons/send';
import Upload from '@weave-brand/icon/icons/upload';
import { ArrowOutward } from '@mui/icons-material';
import { generatedId } from '@/utils/generateId';
import {
  extractChartMetadata,
  formatConversationHistory,
  createStreamingHandler,
  toPlainText,
} from '@/utils/chatUtils';
import { ChatHistory } from './ChatHistory';
import AssistantIcon from '@/components/icons/AssistantIcon';
import AnimatedAssistantIcon from '@/components/icons/AnimatedAssistantIcon';
import { SkillsPage } from '@/components/skills/SkillsPage';
import { SkillChipStrip } from '@/components/skills/SkillChipStrip';
import { SkillPicker } from '@/components/skills/SkillPicker';
import {
  SkillSlashMenu,
  type SkillSlashMenuHandle,
} from '@/components/skills/SkillSlashMenu';
import type { ManualSkill } from '@Types/skills';
import { listSkills } from '@/services/skillsApi';
import { KnowledgePage } from '@/components/knowledge/KnowledgePage';
import { ContextChipStrip } from '@/components/knowledge/ContextChipStrip';
import { ContextPicker } from '@/components/knowledge/ContextPicker';
import type { ManualContextNote } from '@Types/knowledge';
import { isContextSelectable } from '@/utils/knowledgeStatus';
import { routeSkill } from '@/services/jevRouter';
import {
  fetchChatHistory,
  deleteChatConversation,
  clearAllChatHistory,
  fetchConversationMessages,
  transformToMessages,
} from '@/utils/chatHistoryUtils';
import type { Conversation } from '@/types/chat';
import {
  SUGGESTED_CATEGORIES,
  PROMPTS_BY_CATEGORY,
} from '@/constants/suggestedActions';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
  trackAdpWaypoint,
} from '@/services/analytics/adp';
import { BERNOULLI_EMBED_TENANT_CHANGED_EVENT } from '@/services/embedHostContext';

export const ChatInterface = () => {
  const trackCommand = (
    eventName: string,
    attributes: Record<string, string> = {},
  ) => {
    trackAdpEvent(getBernoulliAdp(), eventName, attributes);
  };

  const trackWaypoint = (
    eventName: string,
    attributes: Record<string, string> = {},
  ) => {
    trackAdpWaypoint(getBernoulliAdp(), eventName, attributes);
  };
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [fileUploads, setFileUploads] = useState<FileUploadState[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [conversationId, setConversationId] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const isProgrammaticScrollRef = useRef(false);
  const [chatHistory, setChatHistory] = useState<Conversation[]>([]);
  const [isRestoring, setIsRestoring] = useState(false);
  const uploadAbortControllersRef = useRef<Map<string, AbortController>>(
    new Map(),
  );
  const pendingRequestRef = useRef<{
    input: string;
    messagesLengthBefore: number;
  } | null>(null);
  const pendingExtrasRef = useRef<Record<string, any> | undefined>(undefined);
  // Model context published by MCP Apps views via ui/update-model-context. Keyed by
  // resource URI, so a new update from the same view replaces its previous one. It
  // rides along with the next user message from any path and is consumed there.
  const pendingModelContextRef = useRef<Map<string, string>>(new Map());
  const pendingAutoSubmitRef = useRef(false);
  const dragCounter = useRef(0);
  const isMountedRef = useRef(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSkillsPage, setShowSkillsPage] = useState(false);
  const [showKnowledgePage, setShowKnowledgePage] = useState(false);
  const [activeSkills, setActiveSkills] = useState<ManualSkill[]>([]);
  const [activeContext, setActiveContext] = useState<ManualContextNote[]>([]);
  const [pickerOpenLanding, setPickerOpenLanding] = useState(false);
  const [pickerOpenChat, setPickerOpenChat] = useState(false);
  const [contextPickerOpenLanding, setContextPickerOpenLanding] =
    useState(false);
  const [contextPickerOpenChat, setContextPickerOpenChat] = useState(false);
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const slashMenuRef = useRef<SkillSlashMenuHandle>(null);
  const skillsButtonLandingRef = useRef<HTMLButtonElement>(null);
  const skillsButtonChatRef = useRef<HTMLButtonElement>(null);
  const contextButtonLandingRef = useRef<HTMLButtonElement>(null);
  const contextButtonChatRef = useRef<HTMLButtonElement>(null);

  const activeSkillIds = useMemo(
    () => new Set(activeSkills.map(s => s.skill_id)),
    [activeSkills],
  );

  const toggleSkill = useCallback((skill: ManualSkill) => {
    setActiveSkills(prev => {
      const exists = prev.find(s => s.skill_id === skill.skill_id);
      if (exists) return prev.filter(s => s.skill_id !== skill.skill_id);
      return [...prev, skill];
    });
  }, []);

  const removeSkill = useCallback((skillId: string) => {
    setActiveSkills(prev => prev.filter(s => s.skill_id !== skillId));
  }, []);

  const activeContextIds = useMemo(
    () => new Set(activeContext.map(c => c.context_id)),
    [activeContext],
  );

  const toggleContext = useCallback((note: ManualContextNote) => {
    setActiveContext(prev => {
      const exists = prev.find(c => c.context_id === note.context_id);
      if (exists) return prev.filter(c => c.context_id !== note.context_id);
      if (!isContextSelectable(note)) return prev;
      return [...prev, note];
    });
  }, []);

  const removeContext = useCallback((contextId: string) => {
    setActiveContext(prev => prev.filter(c => c.context_id !== contextId));
  }, []);

  const hydrateActiveSkillsFromIds = useCallback(async (skillIds: string[]) => {
    if (skillIds.length === 0) {
      setActiveSkills([]);
      return;
    }
    try {
      const skillsRes = await listSkills();
      const byId = new Map<string, ManualSkill>([
        ...skillsRes.user.map(s => [s.skill_id, s] as [string, ManualSkill]),
        ...skillsRes.tenant.map(s => [s.skill_id, s] as [string, ManualSkill]),
      ]);
      setActiveSkills(
        skillIds.map(id => byId.get(id)).filter((s): s is ManualSkill => !!s),
      );
    } catch (err) {
      console.warn('Failed to hydrate active skills', err);
      setActiveSkills([]);
    }
  }, []);
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);

  const handleInputChange = useCallback(
    (val: string) => {
      setInput(val);
      if (slashMenuOpen && !val.startsWith('/')) {
        setSlashMenuOpen(false);
      }
    },
    [slashMenuOpen],
  );

  const slashQuery = useMemo(
    () => (slashMenuOpen && input.startsWith('/') ? input.slice(1) : ''),
    [slashMenuOpen, input],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!window.location.pathname.startsWith('/embed')) {
      return;
    }

    const onEmbedTenantChanged = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      pendingRequestRef.current = null;
      pendingModelContextRef.current.clear();

      setMessages([]);
      setError(null);
      setConversationId('');
      setOpenCategoryId(null);
      setIsLoading(false);
      setStreamingStatus('');
      setInput('');
    };

    window.addEventListener(
      BERNOULLI_EMBED_TENANT_CHANGED_EVENT,
      onEmbedTenantChanged,
    );
    return () => {
      window.removeEventListener(
        BERNOULLI_EMBED_TENANT_CHANGED_EVENT,
        onEmbedTenantChanged,
      );
    };
  }, []);

  useEffect(() => {
    const loadChatHistory = async () => {
      const conversations = await fetchChatHistory();
      setChatHistory(conversations);
    };

    loadChatHistory();
  }, []);

  const handleDeleteConversation = async (conversationIdToDelete: string) => {
    trackWaypoint(ADP_EVENTS.CHAT_HISTORY_DELETE, {
      conversation_id: conversationIdToDelete,
    });

    const activeAtDeleteTime = conversationId;

    const success = await deleteChatConversation(conversationIdToDelete);
    if (success) {
      setChatHistory(prev =>
        prev.filter(conv => conv.conversation_id !== conversationIdToDelete),
      );

      if (conversationIdToDelete === activeAtDeleteTime) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        pendingRequestRef.current = null;
        pendingModelContextRef.current.clear();

        setMessages([]);
        setError(null);
        setConversationId('');
        setOpenCategoryId(null);
        setIsLoading(false);
        setStreamingStatus('');
        setInput('');
      }
    }
  };

  const handleClearHistory = async () => {
    trackWaypoint(ADP_EVENTS.CHAT_HISTORY_CLEAR, {});

    const success = await clearAllChatHistory();
    if (success) {
      setChatHistory([]);
    }
  };

  const handleSelectConversation = async (selectedConversationId: string) => {
    trackWaypoint(ADP_EVENTS.CHAT_HISTORY_OPEN, {
      conversation_id: selectedConversationId,
    });

    try {
      setIsRestoring(true);
      setError(null);
      pendingModelContextRef.current.clear();
      setMessages([]); // Clear current messages while loading

      const response = await fetchConversationMessages(selectedConversationId);
      if (response) {
        const restoredMessages = transformToMessages(response);
        setMessages(restoredMessages);
        setConversationId(selectedConversationId);

        const turns = (response.messages || []) as Array<{
          requested_at?: string;
          active_skills_input?: string[];
        }>;
        const latestTurn = turns
          .filter(t => Array.isArray(t.active_skills_input))
          .sort((a, b) =>
            (b.requested_at || '').localeCompare(a.requested_at || ''),
          )[0];
        hydrateActiveSkillsFromIds(latestTurn?.active_skills_input || []);
      } else {
        trackWaypoint(ADP_EVENTS.CHAT_HISTORY_LOAD_ERROR, {
          conversation_id: selectedConversationId,
        });
        setError('Failed to load conversation');
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      trackWaypoint(ADP_EVENTS.CHAT_HISTORY_LOAD_ERROR, {
        conversation_id: selectedConversationId,
      });
      setError('Failed to load conversation');
    } finally {
      setIsRestoring(false);
    }
  };

  // Auto-scroll only when enabled (user clicked scroll-to-bottom button)
  useEffect(() => {
    if (isAutoScrollEnabled && bottomRef.current) {
      isProgrammaticScrollRef.current = true;
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
      // Reset flag after animation completes (smooth scroll takes ~300-500ms)
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 500);
    }
  }, [messages, isLoading, isAutoScrollEnabled]);

  // Handle scroll events to show/hide scroll-to-bottom button
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    // Ignore scroll events triggered by programmatic scrolling
    if (isProgrammaticScrollRef.current) {
      return;
    }

    const target = event.currentTarget;
    const isAtBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 100;

    // Show button when not at bottom
    setShowScrollButton(!isAtBottom);

    // If user scrolled away from bottom, disable auto-scroll
    if (!isAtBottom) {
      setIsAutoScrollEnabled(false);
    }
  }, []);

  // Scroll to bottom and enable auto-scroll
  const handleScrollToBottom = useCallback(() => {
    trackCommand(ADP_EVENTS.CHAT_SCROLL_TO_BOTTOM);
    isProgrammaticScrollRef.current = true;
    setIsAutoScrollEnabled(true);
    setShowScrollButton(false);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Reset flag after animation completes
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
  }, []);

  // Check if any files are currently uploading
  const isUploading = fileUploads.some(
    f => f.status === 'pending' || f.status === 'uploading',
  );

  // Get successfully uploaded files
  const completedUploads = fileUploads.filter(
    f => f.status === 'completed' && f.result,
  );

  // Start uploading a file immediately
  const startFileUpload = useCallback(async (file: File) => {
    const uploadId = generatedId();
    const abortController = new AbortController();
    uploadAbortControllersRef.current.set(uploadId, abortController);

    // Add to state as pending
    const initialState: FileUploadState = {
      id: uploadId,
      file,
      status: 'pending',
      progress: 0,
    };

    setFileUploads(prev => [...prev, initialState]);

    try {
      const result = await uploadAttachment(
        file,
        state => {
          if (!isMountedRef.current) return;
          setFileUploads(prev =>
            prev.map(f => (f.id === uploadId ? { ...f, ...state } : f)),
          );
        },
        abortController.signal,
      );

      if (!isMountedRef.current) return;

      setFileUploads(prev =>
        prev.map(f =>
          f.id === uploadId
            ? { ...f, status: 'completed', progress: 100, result }
            : f,
        ),
      );
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Upload was cancelled, remove from list
        setFileUploads(prev => prev.filter(f => f.id !== uploadId));
        return;
      }

      if (!isMountedRef.current) return;

      trackWaypoint(ADP_EVENTS.CHAT_UPLOAD_ERROR, {
        conversation_id: conversationId || 'none',
      });
      setFileUploads(prev =>
        prev.map(f =>
          f.id === uploadId
            ? {
                ...f,
                status: 'error',
                error: err instanceof Error ? err.message : 'Upload failed',
              }
            : f,
        ),
      );
    } finally {
      uploadAbortControllersRef.current.delete(uploadId);
    }
  }, []);

  const isDrainDetectiveEnabled = !!useIsFeatureEnabled(
    'info360-bernoulli-drain-detective-enabled',
  );
  const isAssetImportEnabled = !!useIsFeatureEnabled(
    'info360-asset-bernoulli-import',
  );
  // TEMPORARY (I360AI-659): restore info360-bernoulli-knowledge-preview gate before merge.
  const isKnowledgeEnabled = true;

  // Handle new files - start uploading immediately
  const handleFilesAdded = useCallback(
    (files: FileList | File[]) => {
      let fileArray = Array.from(files);

      // Block video uploads when Drain Detective feature is disabled
      if (!isDrainDetectiveEnabled) {
        const blocked = fileArray.filter(isVideoFile);
        if (blocked.length > 0) {
          trackWaypoint(ADP_EVENTS.CHAT_UPLOAD_BLOCKED, {
            reason: 'video_feature_disabled',
            file_count: String(blocked.length),
          });
          setError('Video uploads are not supported.');
          fileArray = fileArray.filter(f => !isVideoFile(f));
          if (fileArray.length === 0) return;
        }
      }

      // Block GeoJSON/Shapefile uploads when Asset Import feature is disabled
      if (!isAssetImportEnabled) {
        const blocked = fileArray.filter(isImportFile);
        if (blocked.length > 0) {
          trackWaypoint(ADP_EVENTS.CHAT_UPLOAD_BLOCKED, {
            reason: 'import_feature_disabled',
            file_count: String(blocked.length),
          });
          setError(
            'GeoJSON and Shapefile import is not available for your account at this time.',
          );
          fileArray = fileArray.filter(f => !isImportFile(f));
          if (fileArray.length === 0) return;
        }
      }

      // Start uploading each file immediately
      fileArray.forEach(file => {
        startFileUpload(file);
      });
    },
    [startFileUpload, isDrainDetectiveEnabled, isAssetImportEnabled],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    handleFilesAdded(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (uploadId: string) => {
    trackWaypoint(ADP_EVENTS.CHAT_UPLOAD_REMOVE, {
      conversation_id: conversationId || 'none',
    });

    // If still uploading, abort it
    const abortController = uploadAbortControllersRef.current.get(uploadId);
    if (abortController) {
      abortController.abort();
    }

    // Remove from state
    setFileUploads(prev => prev.filter(f => f.id !== uploadId));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      trackWaypoint(ADP_EVENTS.CHAT_UPLOAD_DROP, {
        file_count: String(files.length),
        conversation_id: conversationId || 'none',
      });
      handleFilesAdded(files);
    }
  };

  const handleSendMessage = async (
    overrideMessage?: string,
    hiddenContext?: string,
    options?: { fromComposer?: boolean },
  ) => {
    // Only a composer send owns the staged uploads and clears the box.
    const fromComposer = options?.fromComposer ?? true;
    const uploadsToSend = fromComposer ? completedUploads : [];
    const messageToSend = overrideMessage ?? input;

    if (fromComposer && isUploading) return;
    if (!messageToSend.trim() && uploadsToSend.length === 0) return;
    if (isLoading) return;

    setOpenCategoryId(null);

    let currentConversationId = conversationId;
    const isNewConversation = !currentConversationId;
    if (isNewConversation) {
      currentConversationId = generatedId();
      setConversationId(currentConversationId);
    }

    const requestId = generatedId();
    const currentInput = messageToSend.trim();
    trackWaypoint(ADP_EVENTS.CHAT_SEND_MESSAGE, {
      conversation_id: currentConversationId,
      has_attachments: String(uploadsToSend.length > 0),
      prompt_length: String(currentInput.length),
    });

    // Add new conversation to chat history immediately
    if (isNewConversation) {
      let historyLabel = currentInput;
      if (!historyLabel) {
        historyLabel =
          uploadsToSend.length === 1
            ? `${uploadsToSend[0].result?.filename ?? 'File'} Uploaded`
            : `${uploadsToSend.length} Files Uploaded`;
      }
      const newConversation: Conversation = {
        conversation_id: currentConversationId,
        initial_prompt: historyLabel,
        requested_at: new Date().toISOString(),
      };
      setChatHistory(prev => [newConversation, ...prev]);
    }
    const attachments: PromptAttachment[] = uploadsToSend.flatMap(f =>
      f.result
        ? [
            {
              filename: f.result.filename,
              s3_key: f.result.s3_key,
              size: f.result.size,
              headers: f.result.headers,
              geojson_properties: f.result.geojson_properties,
              feature_count: f.result.feature_count,
              geometry_type: f.result.geometry_type,
              file_type: f.result.file_type,
            },
          ]
        : [],
    );

    // Build user message content
    const userContent = currentInput || (attachments.length > 0 ? '' : '');

    const contextExtras =
      activeContext.length > 0
        ? {
            context: activeContext.map(c => ({
              context_id: c.context_id,
              name: c.name,
              scope: c.scope,
            })),
          }
        : undefined;

    const userMessage: ChatMessageType = {
      role: 'user',
      content: userContent,
      timestamp: new Date().toISOString(),
      request_id: requestId,
      extras:
        attachments.length > 0 || contextExtras
          ? {
              ...(attachments.length > 0 && {
                files: attachments.map(a => ({
                  filename: a.filename,
                  size: a.size,
                  s3_key: a.s3_key,
                  headers: a.headers,
                })),
              }),
              ...contextExtras,
            }
          : undefined,
    };

    pendingRequestRef.current = {
      input: currentInput,
      messagesLengthBefore: messages.length,
    };

    setMessages(prev => [...prev, userMessage]);
    const baseLength = messages.length + 1;

    const chartMetadata = extractChartMetadata(messages);
    const conversationHistory = formatConversationHistory(messages);

    if (fromComposer) {
      setInput('');
      setFileUploads([]);
    }
    setIsLoading(true);
    setStreamingStatus('Thinking...');
    setError(null);
    // Re-enable auto-scroll when user sends a message
    setIsAutoScrollEnabled(true);
    const streamingMessageIndex = baseLength;
    const streamingMessage: ChatMessageType = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      request_id: requestId,
      blocks: [],
    };
    setMessages(prev => [...prev, streamingMessage]);

    let skillsForRequest = activeSkills;
    if (skillsForRequest.length === 0 && currentInput) {
      try {
        const { user, tenant } = await listSkills();
        const routed = await routeSkill(currentInput, [...tenant, ...user]);
        if (routed) {
          skillsForRequest = [routed];
          setActiveSkills([routed]);
        }
      } catch {
        // Routing is best-effort; never block sending on it.
      }
    }

    abortControllerRef.current = new AbortController();

    try {
      // Send prompt with attachments
      const mergedExtras = {
        ...(chartMetadata.length > 0 && { previous_charts: chartMetadata }),
        ...pendingExtrasRef.current,
      };
      // Context an MCP Apps view published since the last send. Consumed once:
      // views publish one-shot instructions ("call this tool now"), so replaying
      // them on later turns would re-trigger the tool every message.
      const publishedContext = Array.from(
        pendingModelContextRef.current.values(),
      ).filter(Boolean);
      const contextToSend =
        hiddenContext ||
        (publishedContext.length > 0
          ? publishedContext.join('\n\n')
          : undefined);
      const promptRequest = {
        input: currentInput || 'Please analyze the attached files.',
        ...(contextToSend && { additional_context: contextToSend }),
        conversation_id: currentConversationId,
        request_id: requestId,
        conversation_history: conversationHistory,
        ...(attachments.length > 0 && { attachments }),
        ...(skillsForRequest.length > 0 && {
          active_skills: skillsForRequest.map(s => s.skill_id),
        }),
        ...(activeContext.length > 0 && {
          active_context: activeContext.map(c => c.context_id),
        }),
        extras: Object.keys(mergedExtras).length > 0 ? mergedExtras : undefined,
      };
      // Clear pending extras and consumed view context after building request
      pendingExtrasRef.current = undefined;
      pendingModelContextRef.current.clear();

      const streamingHandler = createStreamingHandler(
        streamingMessageIndex,
        requestId,
        isMountedRef,
        setStreamingStatus,
        setMessages,
      );

      const finalAssistant = await sendPrompt(
        promptRequest,
        streamingHandler,
        abortControllerRef.current.signal,
      );

      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev];
          if (updated[streamingMessageIndex]) {
            updated[streamingMessageIndex] = {
              ...finalAssistant,
              request_id: requestId,
            } as any;
          }
          return updated;
        });
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      console.error('Failed to send message:', err);
      if (!isMountedRef.current) return;

      trackWaypoint(ADP_EVENTS.CHAT_SEND_ERROR, {
        conversation_id: currentConversationId,
      });
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to send message. Please try again.',
      );

      setMessages(prev => {
        const newMessages = [...prev];
        const existing = newMessages[streamingMessageIndex];
        const hasPartialResponse =
          !!toPlainText(existing?.content).trim() ||
          (existing?.blocks?.length ?? 0) > 0 ||
          (existing?.reasoningSteps?.length ?? 0) > 0 ||
          (existing?.extras?.uiResources?.length ?? 0) > 0 ||
          !!existing?.transparencySummary?.whatIDid?.trim();

        if (hasPartialResponse) {
          newMessages[streamingMessageIndex] = {
            ...existing,
            role: 'assistant',
            messageCompleted: true,
            timestamp: new Date().toISOString(),
          } as ChatMessageType;
        } else {
          newMessages[streamingMessageIndex] = {
            role: 'assistant',
            content:
              'Sorry, I encountered an error processing your request. Please try again.',
            timestamp: new Date().toISOString(),
          };
        }
        return newMessages;
      });
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        setStreamingStatus('');
        textareaRef.current?.focus();
        abortControllerRef.current = null;
        pendingRequestRef.current = null;
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashMenuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        slashMenuRef.current?.moveHighlight(1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        slashMenuRef.current?.moveHighlight(-1);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const picked = slashMenuRef.current?.selectHighlighted();
        if (picked) {
          setInput('');
          setSlashMenuOpen(false);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashMenuOpen(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
      return;
    }
    if (e.key === '/' && input === '') {
      trackCommand(ADP_EVENTS.SKILLS_SLASH_MENU_OPEN);
      setSlashMenuOpen(true);
    }
  };

  const handleNewChat = () => {
    trackCommand(ADP_EVENTS.NAV_NEW_CHAT, {
      previous_conversation_id: conversationId || 'none',
    });

    pendingModelContextRef.current.clear();
    setMessages([]);
    setError(null);
    setConversationId('');
    setOpenCategoryId(null);
    setActiveSkills([]);
  };

  const userContext = useSelectUserContext();
  const userDisplayName = userContext?.name?.trim()?.split(/\s+/)[0] ?? 'there';

  const handleToggleSidebar = useCallback(
    (method: 'click' | 'escape' = 'click') => {
      trackCommand(ADP_EVENTS.CHAT_HISTORY_SIDEBAR_TOGGLE, {
        state: sidebarOpen ? 'closed' : 'open',
        method,
      });
      setSidebarOpen(prev => !prev);
    },
    [sidebarOpen],
  );

  const handleSelectPrompt = useCallback(
    (prompt: string, extras?: Record<string, any>, autoSubmit?: boolean) => {
      setInput(prompt);
      pendingExtrasRef.current = extras;
      if (autoSubmit) {
        pendingAutoSubmitRef.current = true;
      } else {
        textareaRef.current?.focus();
      }
    },
    [],
  );

  // Edits go out as a new turn, text-only, leaving the composer untouched.
  const handleEditPrompt = (prompt: string) => {
    void handleSendMessage(prompt, undefined, { fromComposer: false });
  };

  // Auto-submit: when handleSelectPrompt is called with autoSubmit=true,
  // the input state update triggers this effect to send the message automatically.
  useEffect(() => {
    if (pendingAutoSubmitRef.current && input.trim()) {
      pendingAutoSubmitRef.current = false;
      handleSendMessage();
    }
  }, [input]); // eslint-disable-line

  const handleComponentAction = useCallback(
    async (action: string, data: unknown) => {
      if (action === 'ui-prompt') {
        const promptData = data as { prompt: string; context?: string };
        await handleSendMessage(promptData.prompt, promptData.context);
        return;
      }
      if (action === 'ui-model-context') {
        const { resourceUri, context } = data as {
          resourceUri?: string;
          context?: string;
        };
        const key = resourceUri ?? '';
        if (context) {
          pendingModelContextRef.current.set(key, context);
        } else {
          pendingModelContextRef.current.delete(key);
        }
      }
    },
    [handleSendMessage],
  );

  const openCategory = openCategoryId
    ? SUGGESTED_CATEGORIES.find(c => c.id === openCategoryId)
    : null;
  const openCategoryPrompts = openCategoryId
    ? PROMPTS_BY_CATEGORY[openCategoryId] ?? []
    : [];

  const handleCancel = () => {
    if (abortControllerRef.current) {
      trackCommand(ADP_EVENTS.CHAT_CANCEL_GENERATION, {
        conversation_id: conversationId || 'none',
      });

      abortControllerRef.current.abort();
      abortControllerRef.current = null;

      const pendingRequest = pendingRequestRef.current;
      pendingRequestRef.current = null;

      if (pendingRequest) {
        const { input, messagesLengthBefore } = pendingRequest;

        setInput(input);
        setMessages(prev => prev.slice(0, messagesLengthBefore));
      }

      setIsLoading(false);
      setStreamingStatus('');
    }
  };

  const isSendDisabled =
    isUploading ||
    isLoading ||
    (!input.trim() && completedUploads.length === 0);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="h-full w-full flex flex-col bg-gradient-to-br from-slate-50 via-slate-100 to-gray-100 overflow-hidden relative isolate"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/5 backdrop-blur-sm">
          <div className="text-center p-8 rounded-2xl bg-white/90 border-2 border-[#3C3C3C] border-dashed shadow-xl">
            <Paperclip className="h-12 w-12 sm:h-14 sm:w-14 mx-auto mb-3 text-[#3C3C3C]" />
            <p className="text-lg sm:text-xl font-semibold text-[#252F33] mb-1">
              Drop files here
            </p>
            <p className="text-sm text-[#768D95]">All file types supported</p>
          </div>
        </div>
      )}
      {isRestoring && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="text-center p-8">
            <div className="relative mx-auto mb-4">
              <div className="w-12 h-12 rounded-full border-4 border-slate-200 border-t-[#3C3C3C] animate-spin" />
            </div>
            <p className="text-lg font-semibold text-[#252F33] mb-1">
              Loading Conversation
            </p>
            <p className="text-sm text-[#768D95]">
              Restoring messages and history...
            </p>
          </div>
        </div>
      )}
      <div className="flex h-full w-full bg-white">
        <ChatHistory
          conversations={chatHistory}
          isOpen={sidebarOpen}
          onToggle={handleToggleSidebar}
          onNewChat={handleNewChat}
          onOpenSkills={() => {
            trackWaypoint(ADP_EVENTS.SKILLS_OPEN_PAGE, { source: 'sidebar' });
            setShowSkillsPage(true);
            if (sidebarOpen) setSidebarOpen(false);
          }}
          onOpenKnowledge={
            isKnowledgeEnabled
              ? () => {
                  trackWaypoint(ADP_EVENTS.NAV_OPEN_KNOWLEDGE, {
                    source: 'sidebar',
                  });
                  setShowKnowledgePage(true);
                  if (sidebarOpen) setSidebarOpen(false);
                }
              : undefined
          }
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onClearHistory={handleClearHistory}
        />

        <div className="flex flex-col flex-1 min-w-0 bg-[#F9FAFC] relative">
          {showSkillsPage && (
            <SkillsPage
              onClose={() => {
                trackCommand(ADP_EVENTS.SKILLS_CLOSE_PAGE);
                setShowSkillsPage(false);
              }}
            />
          )}
          {isKnowledgeEnabled && showKnowledgePage && (
            <KnowledgePage onClose={() => setShowKnowledgePage(false)} />
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            aria-hidden
          />
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-[#F9FAFC] flex-shrink-0 relative border-b border-slate-200/60">
            <div className="flex items-center gap-2">
              <AssistantIcon
                size={20}
                className="text-[#161618] flex-shrink-0"
              />
              <h1 className="text-[15px] font-medium text-[#161618] leading-tight">
                Autodesk Assistant
              </h1>
            </div>

            {conversationId && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[50%] w-full">
                <div className="flex flex-col items-center gap-0.5 justify-center">
                  <span className="text-sm font-medium text-[#161618] truncate min-w-0 max-w-full">
                    {chatHistory.find(c => c.conversation_id === conversationId)
                      ?.initial_prompt ?? ''}
                  </span>
                  <span className="text-[11px] text-[#A0AEB4] font-mono truncate min-w-0 max-w-full">
                    Conversation ID: {conversationId}
                  </span>
                </div>
              </div>
            )}

            <div className="w-20 sm:w-24" aria-hidden />
          </div>

          <div className="flex-1 overflow-hidden relative bg-[#F9FAFC]">
            {error && messages.length === 0 && (
              <div className="max-w-7xl mx-auto w-full px-3 sm:px-4 md:px-6 lg:px-8 pt-2 sm:pt-3 flex-shrink-0">
                <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-red-50 border border-[#E66B45] flex items-start justify-between gap-3">
                  <p className="text-sm text-[#E66B45] flex-1">{error}</p>
                  <button
                    onClick={() => {
                      trackCommand(ADP_EVENTS.CHAT_ERROR_DISMISS);
                      setError(null);
                    }}
                    className="flex-shrink-0 text-[#E66B45] hover:text-[#d15a39] transition-colors"
                    aria-label="Close error message">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <ScrollArea
              className="h-full relative"
              viewportRef={scrollViewportRef}
              onScroll={handleScroll}>
              <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 min-h-full bg-[#F9FAFC]">
                {messages.length === 0 ? (
                  <div className="min-h-[calc(100vh-12rem)] w-full flex flex-col items-center pt-[18vh] pb-8">
                    <div className="w-full max-w-3xl">
                      <div className="flex flex-col items-start text-left mb-5">
                        <div className="flex items-center gap-2 mb-1">
                          {/* The empty state is what a fresh load and every New
                              Chat land on, and it mounts only here — so the
                              mark plays its build-up exactly on those two
                              occasions. */}
                          <AnimatedAssistantIcon
                            size={32}
                            className="text-[#3C3C3C]"
                          />
                          <p className="text-[18px] font-normal text-[#161618] leading-tight tracking-tight">
                            Hi, {userDisplayName}
                          </p>
                        </div>
                        <h2 className="text-[22px] font-semibold text-[#161618] leading-tight tracking-tight">
                          How can I help you today?
                        </h2>
                      </div>

                      <div className="w-full mb-0">
                        {fileUploads.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {fileUploads.map(uploadState => (
                              <FileUploadItem
                                key={uploadState.id}
                                uploadState={uploadState}
                                onRemove={() =>
                                  handleRemoveFile(uploadState.id)
                                }
                                disabled={isLoading}
                              />
                            ))}
                          </div>
                        )}
                        <SkillChipStrip
                          active={activeSkills}
                          onRemove={removeSkill}
                        />
                        {isKnowledgeEnabled && (
                          <ContextChipStrip
                            active={activeContext}
                            onRemove={removeContext}
                          />
                        )}
                        <div className="rounded-2xl bg-[#FFFFFF] border border-slate-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-200 transition-all overflow-hidden flex flex-col">
                          <div className="flex-1 min-h-[72px]">
                            <Textarea
                              ref={textareaRef}
                              value={input}
                              onChange={e => handleInputChange(e.target.value)}
                              onKeyDown={handleKeyDown}
                              placeholder="Ask me anything..."
                              className="min-h-[72px] max-h-[200px] !border-0 !shadow-none focus-visible:ring-0 bg-transparent text-base font-normal px-4 pt-3 pb-1 rounded-none text-[#161618] placeholder:text-[#C1C6C7] placeholder:text-base placeholder:font-normal resize-none w-full"
                            />
                          </div>
                          <div className="flex items-center justify-between px-2 pb-2 pt-0 bg-[#FFFFFF] border-t-0">
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  trackWaypoint(ADP_EVENTS.CHAT_UPLOAD_OPEN, {
                                    context: 'landing',
                                  });
                                  fileInputRef.current?.click();
                                }}
                                className="h-8 w-8 rounded-full bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576] flex-shrink-0 border-0 shadow-none">
                                <Upload className="h-4 w-4" />
                              </Button>
                              <Button
                                ref={skillsButtonLandingRef}
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const next = !pickerOpenLanding;
                                  trackCommand(
                                    ADP_EVENTS.SKILLS_PICKER_TOGGLE,
                                    {
                                      state: next ? 'open' : 'closed',
                                      context: 'landing',
                                    },
                                  );
                                  setPickerOpenLanding(next);
                                }}
                                aria-label="Skills"
                                className={`h-8 w-8 rounded-full flex-shrink-0 border-0 shadow-none ${
                                  activeSkills.length > 0
                                    ? 'bg-[#E0E0E0] text-[#3C3C3C] hover:bg-[#D4D4D4]'
                                    : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576]'
                                }`}>
                                <Sparkles className="h-4 w-4" />
                              </Button>
                              {pickerOpenLanding && (
                                <SkillPicker
                                  anchorRef={skillsButtonLandingRef}
                                  activeIds={activeSkillIds}
                                  onToggle={toggleSkill}
                                  onClose={() => setPickerOpenLanding(false)}
                                  onOpenLibrary={() => setShowSkillsPage(true)}
                                />
                              )}
                              {isKnowledgeEnabled && (
                                <>
                                  <span className="relative inline-flex">
                                    <Button
                                      ref={contextButtonLandingRef}
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        setContextPickerOpenLanding(
                                          !contextPickerOpenLanding,
                                        )
                                      }
                                      aria-label={
                                        activeContext.length > 0
                                          ? `Knowledge (${activeContext.length} attached)`
                                          : 'Knowledge (optional)'
                                      }
                                      className={`h-8 w-8 rounded-full flex-shrink-0 border-0 shadow-none ${
                                        activeContext.length > 0
                                          ? 'bg-sky-100 text-[#007CA0] hover:bg-sky-200'
                                          : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576]'
                                      }`}>
                                      <BookOpen className="h-4 w-4" />
                                    </Button>
                                    {activeContext.length > 0 && (
                                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-[#007CA0] text-white text-[10px] font-semibold leading-none">
                                        {activeContext.length}
                                      </span>
                                    )}
                                  </span>
                                  {contextPickerOpenLanding && (
                                    <ContextPicker
                                      anchorRef={contextButtonLandingRef}
                                      activeIds={activeContextIds}
                                      onToggle={toggleContext}
                                      onClose={() =>
                                        setContextPickerOpenLanding(false)
                                      }
                                      onOpenLibrary={() =>
                                        setShowKnowledgePage(true)
                                      }
                                    />
                                  )}
                                </>
                              )}
                              {slashMenuOpen && (
                                <SkillSlashMenu
                                  ref={slashMenuRef}
                                  anchorRef={textareaRef}
                                  query={slashQuery}
                                  activeIds={activeSkillIds}
                                  onToggle={skill => {
                                    toggleSkill(skill);
                                    setInput('');
                                  }}
                                  onClose={() => setSlashMenuOpen(false)}
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-1 ml-auto">
                              {input.trim() && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    trackCommand(ADP_EVENTS.CHAT_CLEAR_INPUT);
                                    setInput('');
                                    textareaRef.current?.focus();
                                  }}
                                  className="h-8 w-8 rounded-full text-[#757576] hover:text-[#252F33] hover:bg-[#F3F5F9] flex-shrink-0 border-0 shadow-none"
                                  aria-label="Clear input">
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                type="button"
                                onClick={
                                  isLoading
                                    ? handleCancel
                                    : () => handleSendMessage()
                                }
                                disabled={isSendDisabled && !isLoading}
                                size="icon"
                                className={`h-8 w-8 rounded-full flex-shrink-0 border-0 shadow-none disabled:opacity-100 ${
                                  !isSendDisabled
                                    ? 'bg-[#7A8C94] hover:bg-[#6A7C84] text-white'
                                    : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#7A8C94]'
                                }`}>
                                {isLoading ? (
                                  <X className="h-4 w-4" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                          {openCategoryId !== null && openCategory && (
                            <>
                              <div className="border-t border-slate-200" />
                              <div className="flex items-center justify-between px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <openCategory.icon
                                    className="h-4 w-4 text-[#768D95] flex-shrink-0"
                                    sx={{ fontSize: 18, color: '#768D95' }}
                                  />
                                  <span className="text-sm font-semibold text-[#252F33]">
                                    {openCategory.label}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    trackCommand(
                                      ADP_EVENTS.CHAT_SUGGESTED_CATEGORY_CLOSE,
                                      { category_id: openCategoryId || '' },
                                    );
                                    setOpenCategoryId(null);
                                  }}
                                  className="p-1.5 rounded-md text-[#768D95] hover:text-[#252F33] hover:bg-[#F3F5F9] transition-colors"
                                  aria-label="Close">
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              <ul className="py-1">
                                {openCategoryPrompts.map((prompt, index) => (
                                  <li key={index}>
                                    <button
                                      type="button"
                                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#252F33] w-full bg-transparent border-none cursor-pointer text-left hover:bg-[#F3F5F9] transition-colors"
                                      onClick={() => {
                                        trackWaypoint(
                                          ADP_EVENTS.CHAT_SUGGESTED_CATEGORY_PROMPT,
                                          {
                                            category_id: openCategoryId || '',
                                            prompt_text: prompt,
                                          },
                                        );
                                        handleSelectPrompt(prompt);
                                      }}>
                                      <span className="flex-1 min-w-0 truncate">
                                        {prompt}
                                      </span>
                                      <ArrowOutward className="h-4 w-4 text-[#768D95] flex-shrink-0" />
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      </div>

                      {openCategoryId === null && (
                        <div className="flex flex-nowrap justify-center gap-2 pt-4">
                          {SUGGESTED_CATEGORIES.map(
                            ({ id, label, icon: Icon }) => (
                              <Button
                                key={id}
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  trackWaypoint(
                                    ADP_EVENTS.CHAT_SUGGESTED_CATEGORY,
                                    {
                                      category_id: id,
                                      category_label: label,
                                    },
                                  );
                                  setOpenCategoryId(id);
                                }}
                                className="rounded-lg border border-slate-200 bg-[#F9FAFC] text-[#82949B] hover:bg-[#F3F5F9] hover:border-slate-300 text-sm font-normal py-1.5 px-3 h-auto flex-shrink-0">
                                <Icon
                                  sx={{ fontSize: 14, color: '#82949B' }}
                                  className="mr-1.5 flex-shrink-0"
                                />
                                {label}
                              </Button>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {messages.map((message, index) => {
                      const isStreamingMessage =
                        isLoading &&
                        index === messages.length - 1 &&
                        message.role === 'assistant';

                      return (
                        <ChatMessage
                          key={
                            message.request_id
                              ? `${message.request_id}-${message.role}`
                              : `index-${index}`
                          }
                          message={message}
                          onComponentAction={handleComponentAction}
                          onSelectNextPrompt={handleSelectPrompt}
                          onEditPrompt={
                            isLoading ? undefined : handleEditPrompt
                          }
                          conversation_id={conversationId}
                          streamingStatus={
                            isStreamingMessage
                              ? streamingStatus || 'Thinking...'
                              : undefined
                          }
                        />
                      );
                    })}

                    <div ref={bottomRef} />
                  </div>
                )}
              </div>
            </ScrollArea>

            {showScrollButton && messages.length > 0 && (
              <Button
                onClick={handleScrollToBottom}
                size="icon"
                className="absolute bottom-4 left-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-white border border-slate-200 shadow-lg hover:shadow-xl hover:bg-slate-50 transition-all z-10"
                aria-label="Scroll to bottom">
                <ArrowDown className="h-5 w-5 text-[#768D95]" />
              </Button>
            )}
          </div>

          {messages.length > 0 && (
            <div className="bg-[#F9FAFC] flex-shrink-0">
              {error && (
                <div className="max-w-7xl mx-auto w-full px-3 sm:px-4 md:px-6 lg:px-8 pt-2 sm:pt-3">
                  <div className="p-3 sm:p-4 rounded-lg sm:rounded-xl bg-red-50 border border-[#E66B45] flex items-start justify-between gap-3">
                    <p className="text-sm text-[#E66B45] flex-1">{error}</p>
                    <button
                      onClick={() => {
                        trackCommand(ADP_EVENTS.CHAT_ERROR_DISMISS);
                        setError(null);
                      }}
                      className="flex-shrink-0 text-[#E66B45] hover:text-[#d15a39] transition-colors"
                      aria-label="Close error message">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              <div className="max-w-7xl mx-auto w-full px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
                {fileUploads.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {fileUploads.map(uploadState => (
                      <FileUploadItem
                        key={uploadState.id}
                        uploadState={uploadState}
                        onRemove={() => handleRemoveFile(uploadState.id)}
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                )}

                <SkillChipStrip active={activeSkills} onRemove={removeSkill} />
                {isKnowledgeEnabled && (
                  <ContextChipStrip
                    active={activeContext}
                    onRemove={removeContext}
                  />
                )}

                <div className="rounded-2xl bg-white border border-slate-200 shadow-sm focus-within:border-slate-300 focus-within:ring-1 focus-within:ring-slate-200 transition-all overflow-hidden flex flex-col">
                  <div className="flex-1 min-h-[48px]">
                    <Textarea
                      ref={textareaRef}
                      value={input}
                      onChange={e => handleInputChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Send message to Autodesk Assistant"
                      className="min-h-[48px] max-h-[200px] !border-0 !shadow-none focus-visible:ring-0 bg-transparent text-base font-normal px-4 pt-3 pb-1 rounded-none text-[#161618] placeholder:text-[#C1C6C7] placeholder:text-base placeholder:font-normal resize-none w-full"
                    />
                  </div>
                  <div className="flex items-center justify-between px-2 pb-2 pt-0 bg-white border-t-0">
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          trackWaypoint(ADP_EVENTS.CHAT_UPLOAD_OPEN, {
                            conversation_id: conversationId || 'none',
                            context: 'chat',
                          });
                          fileInputRef.current?.click();
                        }}
                        className="h-8 w-8 rounded-full bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576] flex-shrink-0 border-0 shadow-none">
                        <Upload className="h-4 w-4" />
                      </Button>
                      <Button
                        ref={skillsButtonChatRef}
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const next = !pickerOpenChat;
                          trackCommand(ADP_EVENTS.SKILLS_PICKER_TOGGLE, {
                            state: next ? 'open' : 'closed',
                            context: 'chat',
                          });
                          setPickerOpenChat(next);
                        }}
                        aria-label="Skills"
                        className={`h-8 w-8 rounded-full flex-shrink-0 border-0 shadow-none ${
                          activeSkills.length > 0
                            ? 'bg-[#E0E0E0] text-[#3C3C3C] hover:bg-[#D4D4D4]'
                            : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576]'
                        }`}>
                        <Sparkles className="h-4 w-4" />
                      </Button>
                      {pickerOpenChat && (
                        <SkillPicker
                          anchorRef={skillsButtonChatRef}
                          activeIds={activeSkillIds}
                          onToggle={toggleSkill}
                          onClose={() => setPickerOpenChat(false)}
                          onOpenLibrary={() => setShowSkillsPage(true)}
                        />
                      )}
                      {isKnowledgeEnabled && (
                        <>
                          <span className="relative inline-flex">
                            <Button
                              ref={contextButtonChatRef}
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                setContextPickerOpenChat(!contextPickerOpenChat)
                              }
                              aria-label={
                                activeContext.length > 0
                                  ? `Knowledge (${activeContext.length} attached)`
                                  : 'Knowledge (optional)'
                              }
                              className={`h-8 w-8 rounded-full flex-shrink-0 border-0 shadow-none ${
                                activeContext.length > 0
                                  ? 'bg-sky-100 text-[#007CA0] hover:bg-sky-200'
                                  : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#757576]'
                              }`}>
                              <BookOpen className="h-4 w-4" />
                            </Button>
                            {activeContext.length > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-[#007CA0] text-white text-[10px] font-semibold leading-none">
                                {activeContext.length}
                              </span>
                            )}
                          </span>
                          {contextPickerOpenChat && (
                            <ContextPicker
                              anchorRef={contextButtonChatRef}
                              activeIds={activeContextIds}
                              onToggle={toggleContext}
                              onClose={() => setContextPickerOpenChat(false)}
                              onOpenLibrary={() => setShowKnowledgePage(true)}
                            />
                          )}
                        </>
                      )}
                      {slashMenuOpen && (
                        <SkillSlashMenu
                          ref={slashMenuRef}
                          anchorRef={textareaRef}
                          query={slashQuery}
                          activeIds={activeSkillIds}
                          onToggle={skill => {
                            toggleSkill(skill);
                            setInput('');
                          }}
                          onClose={() => setSlashMenuOpen(false)}
                        />
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={
                        isLoading ? handleCancel : () => handleSendMessage()
                      }
                      disabled={isSendDisabled && !isLoading}
                      size="icon"
                      className={`h-8 w-8 rounded-full flex-shrink-0 ml-auto border-0 shadow-none disabled:opacity-100 ${
                        !isSendDisabled
                          ? 'bg-[#7A8C94] hover:bg-[#6A7C84] text-white'
                          : 'bg-[#F9FAFC] hover:bg-[#F3F5F9] text-[#7A8C94]'
                      }`}>
                      {isLoading ? (
                        <X className="h-4 w-4" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-[#768D95] mt-2 text-center">
                  Autodesk Assistant is AI and can make mistakes. Double-check
                  responses.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
